import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  faArrowLeft,
  faBan,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faCrown,
  faEdit,
  faPlus,
  faRefresh,
  faSearch,
  faShieldHalved,
  faTrash,
  faUser,
  faUserCheck,
  faUserGroup,
  faUserSlash,
} from "@fortawesome/free-solid-svg-icons";

import toast from "react-hot-toast";

import {
  dashboardUsersApi,
} from "../api/dashboardUsersApi";

import type {
  DashboardUserItem,
  DashboardUsersStats,
} from "../types/dashboard-users.types";

import {
  useAuth,
} from "../contexts/AuthContext";

import "../screenstyles/DashboardUsers.css";

type ActiveFilter =
  | "all"
  | "active"
  | "inactive";

type SuperAdminFilter =
  | "all"
  | "yes"
  | "no";

type SortOption =
  | "createdAt_desc"
  | "createdAt_asc"
  | "updatedAt_desc"
  | "updatedAt_asc"
  | "name_asc"
  | "name_desc"
  | "email_asc"
  | "email_desc";

const DEFAULT_STATS: DashboardUsersStats = {
  total: 0,
  active: 0,
  inactive: 0,
  superAdmins: 0,
  withRole: 0,
  withoutRole: 0,
};

function formatDate(
  value: string | null,
): string {
  if (!value) {
    return "—";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "ar-IQ",
    {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    },
  ).format(date);
}

function getInitials(
  name: string,
): string {
  const cleaned =
    name.trim();

  if (!cleaned) {
    return "U";
  }

  const parts =
    cleaned
      .split(/\s+/)
      .filter(Boolean);

  if (
    parts.length === 1
  ) {
    return parts[0]
      .slice(0, 2)
      .toUpperCase();
  }

  return `${parts[0][0] ?? ""}${
    parts[1][0] ?? ""
  }`.toUpperCase();
}

const DashboardUsers:
React.FC = () => {
  const navigate =
    useNavigate();

  const {
    hasPermission,
  } = useAuth();

  const [
    users,
    setUsers,
  ] = useState<
    DashboardUserItem[]
  >([]);

  const [
    stats,
    setStats,
  ] =
    useState<DashboardUsersStats>(
      DEFAULT_STATS,
    );

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    actionLoadingUid,
    setActionLoadingUid,
  ] =
    useState<string | null>(
      null,
    );

  const [
    search,
    setSearch,
  ] =
    useState("");

  const [
    activeFilter,
    setActiveFilter,
  ] =
    useState<ActiveFilter>(
      "all",
    );

  const [
    superAdminFilter,
    setSuperAdminFilter,
  ] =
    useState<SuperAdminFilter>(
      "all",
    );

  const [
    sort,
    setSort,
  ] =
    useState<SortOption>(
      "createdAt_desc",
    );

  const [
    currentCursor,
    setCurrentCursor,
  ] =
    useState<
      string | undefined
    >(undefined);

  const [
    nextCursor,
    setNextCursor,
  ] =
    useState<string | null>(
      null,
    );

  const [
    hasMore,
    setHasMore,
  ] =
    useState(false);

  const [
    cursorHistory,
    setCursorHistory,
  ] = useState<
    Array<
      string | undefined
    >
  >([]);

  const canCreate =
    hasPermission(
      "dashboard-users.create",
    );

  const canUpdate =
    hasPermission(
      "dashboard-users.update",
    );

  const canManageStatus =
    hasPermission(
      "dashboard-users.status.manage",
    );

  const canDelete =
    hasPermission(
      "dashboard-users.delete",
    );

  const canManagePermissions =
    hasPermission(
      "dashboard-users.permissions.manage",
    );

  const loadStats =
    useCallback(
      async () => {
        try {
         const data =
  await dashboardUsersApi
    .stats();

setStats(
  data.stats ??
    DEFAULT_STATS,
);
        } catch (
          error
        ) {
          console.error(
            "❌ Failed to load dashboard user stats:",
            error,
          );
        }
      },
      [],
    );

  const loadUsers =
    useCallback(
      async () => {
        setLoading(true);

        try {
         const data =
  await dashboardUsersApi
    .list({
      search:
        search.trim() ||
        undefined,

      isActive:
        activeFilter ===
        "all"
          ? undefined
          : activeFilter ===
            "active",

      isSuperAdmin:
        superAdminFilter ===
        "all"
          ? undefined
          : superAdminFilter ===
            "yes",

      sort,

      limit: 20,

      cursor:
        currentCursor,
    });

          setUsers(
            data.items ??
              [],
          );

          setNextCursor(
            data.pageInfo
              ?.nextCursor ??
              null,
          );

          setHasMore(
            Boolean(
              data.pageInfo
                ?.hasMore,
            ),
          );
        } catch (
          error: any
        ) {
          console.error(
            "❌ Failed to load dashboard users:",
            error,
          );

          toast.error(
            error?.message ||
              "فشل تحميل مستخدمي الداش بورد",
          );

          setUsers([]);
          setNextCursor(
            null,
          );
          setHasMore(
            false,
          );
        } finally {
          setLoading(false);
        }
      },
      [
        search,
        activeFilter,
        superAdminFilter,
        sort,
        currentCursor,
      ],
    );

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    setCurrentCursor(
      undefined,
    );

    setCursorHistory(
      [],
    );
  }, [
    search,
    activeFilter,
    superAdminFilter,
    sort,
  ]);

  const handleRefresh =
    async () => {
      await Promise.all([
        loadUsers(),
        loadStats(),
      ]);

      toast.success(
        "تم تحديث البيانات",
      );
    };

  const handleNextPage =
    () => {
      if (
        !hasMore ||
        !nextCursor
      ) {
        return;
      }

      setCursorHistory(
        (prev) => [
          ...prev,
          currentCursor,
        ],
      );

      setCurrentCursor(
        nextCursor,
      );
    };

  const handlePreviousPage =
    () => {
      if (
        cursorHistory
          .length === 0
      ) {
        return;
      }

      const previousCursor =
        cursorHistory[
          cursorHistory.length -
            1
        ];

      setCursorHistory(
        (prev) =>
          prev.slice(
            0,
            -1,
          ),
      );

      setCurrentCursor(
        previousCursor,
      );
    };

  const handleToggleStatus =
    async (
      user:
        DashboardUserItem,
    ) => {
      if (
        !canManageStatus
      ) {
        toast.error(
          "ليس لديك صلاحية لتغيير حالة المستخدم",
        );
        return;
      }

      const nextStatus =
        !user.isActive;

      const confirmed =
        window.confirm(
          nextStatus
            ? `هل تريد تفعيل المستخدم ${user.fullName}؟`
            : `هل تريد تعطيل المستخدم ${user.fullName}؟`,
        );

      if (!confirmed) {
        return;
      }

      setActionLoadingUid(
        user.uid,
      );

      try {
        await dashboardUsersApi
          .updateStatus(
            user.uid,
            nextStatus,
          );

        toast.success(
          nextStatus
            ? "تم تفعيل المستخدم"
            : "تم تعطيل المستخدم",
        );

        await Promise.all([
          loadUsers(),
          loadStats(),
        ]);
      } catch (
        error: any
      ) {
        console.error(
          "❌ Failed to update dashboard user status:",
          error,
        );

        toast.error(
          error?.message ||
            "فشل تغيير حالة المستخدم",
        );
      } finally {
        setActionLoadingUid(
          null,
        );
      }
    };

  const handleDelete =
    async (
      user:
        DashboardUserItem,
    ) => {
      if (!canDelete) {
        toast.error(
          "ليس لديك صلاحية لحذف المستخدم",
        );
        return;
      }

      const confirmed =
        window.confirm(
          `سيتم حذف حساب ${user.fullName} نهائيًا من Firebase Auth وUsersAccount.\n\nهل أنت متأكد؟`,
        );

      if (!confirmed) {
        return;
      }

      setActionLoadingUid(
        user.uid,
      );

      try {
        await dashboardUsersApi
          .delete(
            user.uid,
          );

        toast.success(
          "تم حذف المستخدم",
        );

        await Promise.all([
          loadUsers(),
          loadStats(),
        ]);
      } catch (
        error: any
      ) {
        console.error(
          "❌ Failed to delete dashboard user:",
          error,
        );

        toast.error(
          error?.message ||
            "فشل حذف المستخدم",
        );
      } finally {
        setActionLoadingUid(
          null,
        );
      }
    };

  const emptyMessage =
    useMemo(
      () => {
        if (
          search.trim()
        ) {
          return "لا توجد نتائج مطابقة لعملية البحث";
        }

        if (
          activeFilter !==
            "all" ||
          superAdminFilter !==
            "all"
        ) {
          return "لا يوجد مستخدمون مطابقون للفلاتر الحالية";
        }

        return "لا يوجد مستخدمو داش بورد حتى الآن";
      },
      [
        search,
        activeFilter,
        superAdminFilter,
      ],
    );

  return (
    <div className="dashboard-users-page">
      <div className="dashboard-users-header">
        <div>
          <span className="dashboard-users-eyebrow">
            إدارة النظام
          </span>

          <h1 className="dashboard-users-title">
            مستخدمو الداش بورد
          </h1>

          <p className="dashboard-users-subtitle">
            إدارة حسابات موظفي لوحة التحكم،
            الأدوار، الصلاحيات وحالة الوصول.
          </p>
        </div>

        <div className="dashboard-users-header-actions">
          <button
            type="button"
            className="dashboard-users-icon-button"
            onClick={
              handleRefresh
            }
            title="تحديث"
          >
            <FontAwesomeIcon
              icon={
                faRefresh
              }
            />
          </button>

          {canCreate && (
            <button
              type="button"
              className="dashboard-users-primary-button"
              onClick={() =>
                navigate(
                  "/dashboard-users/add",
                )
              }
            >
              <FontAwesomeIcon
                icon={
                  faPlus
                }
              />

              <span>
                إضافة مستخدم
              </span>
            </button>
          )}

          <button
            type="button"
            className="dashboard-users-back-button"
            onClick={() =>
              navigate(-1)
            }
          >
            <FontAwesomeIcon
              icon={
                faArrowLeft
              }
            />
          </button>
        </div>
      </div>

      <div className="dashboard-users-stats-grid">
        <div className="dashboard-users-stat-card">
          <div className="dashboard-users-stat-icon">
            <FontAwesomeIcon
              icon={
                faUserGroup
              }
            />
          </div>

          <div>
            <strong>
              {stats.total}
            </strong>

            <span>
              إجمالي المستخدمين
            </span>
          </div>
        </div>

        <div className="dashboard-users-stat-card">
          <div className="dashboard-users-stat-icon">
            <FontAwesomeIcon
              icon={
                faUserCheck
              }
            />
          </div>

          <div>
            <strong>
              {stats.active}
            </strong>

            <span>
              المستخدمون النشطون
            </span>
          </div>
        </div>

        <div className="dashboard-users-stat-card">
          <div className="dashboard-users-stat-icon">
            <FontAwesomeIcon
              icon={
                faUserSlash
              }
            />
          </div>

          <div>
            <strong>
              {stats.inactive}
            </strong>

            <span>
              الحسابات المعطلة
            </span>
          </div>
        </div>

        <div className="dashboard-users-stat-card">
          <div className="dashboard-users-stat-icon">
            <FontAwesomeIcon
              icon={
                faCrown
              }
            />
          </div>

          <div>
            <strong>
              {stats.superAdmins}
            </strong>

            <span>
              Super Admin
            </span>
          </div>
        </div>
      </div>

      <div className="dashboard-users-toolbar">
        <div className="dashboard-users-search">
          <FontAwesomeIcon
            icon={
              faSearch
            }
          />

          <input
            type="text"
            value={search}
            onChange={(event) =>
              setSearch(
                event.target
                  .value,
              )
            }
            placeholder="بحث بالاسم، البريد، الهاتف، UID أو الدور..."
          />
        </div>

        <div className="dashboard-users-filters">
          <select
            value={
              activeFilter
            }
            onChange={(event) =>
              setActiveFilter(
                event.target
                  .value as
                  ActiveFilter,
              )
            }
          >
            <option value="all">
              كل الحالات
            </option>

            <option value="active">
              نشط
            </option>

            <option value="inactive">
              معطل
            </option>
          </select>

          <select
            value={
              superAdminFilter
            }
            onChange={(event) =>
              setSuperAdminFilter(
                event.target
                  .value as
                  SuperAdminFilter,
              )
            }
          >
            <option value="all">
              كل مستويات الإدارة
            </option>

            <option value="yes">
              Super Admin
            </option>

            <option value="no">
              مستخدم عادي
            </option>
          </select>

          <select
            value={sort}
            onChange={(event) =>
              setSort(
                event.target
                  .value as
                  SortOption,
              )
            }
          >
            <option value="createdAt_desc">
              الأحدث
            </option>

            <option value="createdAt_asc">
              الأقدم
            </option>

            <option value="name_asc">
              الاسم A-Z
            </option>

            <option value="name_desc">
              الاسم Z-A
            </option>

            <option value="email_asc">
              البريد A-Z
            </option>

            <option value="email_desc">
              البريد Z-A
            </option>

            <option value="updatedAt_desc">
              آخر تعديل
            </option>
          </select>
        </div>
      </div>

      <div className="dashboard-users-table-card">
        {loading ? (
          <div className="dashboard-users-state">
            جاري تحميل المستخدمين...
          </div>
        ) : users.length === 0 ? (
          <div className="dashboard-users-state">
            <FontAwesomeIcon
              icon={faUser}
            />

            <span>
              {emptyMessage}
            </span>
          </div>
        ) : (
          <div className="dashboard-users-table-wrapper">
            <table className="dashboard-users-table">
              <thead>
                <tr>
                  <th>
                    المستخدم
                  </th>

                  <th>
                    الدور
                  </th>

                  <th>
                    الصلاحيات
                  </th>

                  <th>
                    الحالة
                  </th>

                  <th>
                    آخر تسجيل دخول
                  </th>

                  <th>
                    تاريخ الإنشاء
                  </th>

                  <th>
                    الإجراءات
                  </th>
                </tr>
              </thead>

              <tbody>
                {users.map(
                  (user) => {
                    const isActionLoading =
                      actionLoadingUid ===
                      user.uid;

                    return (
                      <tr
                        key={
                          user.uid
                        }
                      >
                        <td>
                          <div className="dashboard-users-user-cell">
                            <div className="dashboard-users-avatar">
                              {user.photoUrl ? (
                                <img
                                  src={
                                    user.photoUrl
                                  }
                                  alt={
                                    user.fullName
                                  }
                                />
                              ) : (
                                <span>
                                  {getInitials(
                                    user.fullName,
                                  )}
                                </span>
                              )}
                            </div>

                            <div className="dashboard-users-user-info">
                              <div className="dashboard-users-user-name-line">
                                <strong>
                                  {user.fullName ||
                                    "بدون اسم"}
                                </strong>

                                {user.isSuperAdmin && (
                                  <span className="dashboard-users-super-badge">
                                    <FontAwesomeIcon
                                      icon={
                                        faCrown
                                      }
                                    />

                                    Super Admin
                                  </span>
                                )}
                              </div>

                              <span>
                                {user.email}
                              </span>

                              {user.phoneNumber && (
                                <small>
                                  {
                                    user.phoneNumber
                                  }
                                </small>
                              )}
                            </div>
                          </div>
                        </td>

                        <td>
                          {user.role ? (
                            <div className="dashboard-users-role-box">
                              <FontAwesomeIcon
                                icon={
                                  faShieldHalved
                                }
                              />

                              <div>
                                <strong>
                                  {user.role
                                    .nameAr ||
                                    user.role
                                      .name}
                                </strong>

                                <span>
                                  {
                                    user.role
                                      .id
                                  }
                                </span>
                              </div>
                            </div>
                          ) : (
                            <span className="dashboard-users-muted">
                              بدون دور
                            </span>
                          )}
                        </td>

                        <td>
                          <div className="dashboard-users-permissions-cell">
                            <strong>
                              {
                                user
                                  .permissions
                                  .effectivePermissionIds
                                  .length
                              }
                            </strong>

                            <span>
                              صلاحية فعالة
                            </span>

                            {user
                              .permissions
                              .directPermissionIds
                              .length >
                              0 && (
                              <small>
                                {
                                  user
                                    .permissions
                                    .directPermissionIds
                                    .length
                                }{" "}
                                مباشرة
                              </small>
                            )}
                          </div>
                        </td>

                        <td>
                          <div className="dashboard-users-status-cell">
                            <span
                              className={
                                user.isActive &&
                                !user.authDisabled
                                  ? "dashboard-users-status dashboard-users-status-active"
                                  : "dashboard-users-status dashboard-users-status-inactive"
                              }
                            >
                              <FontAwesomeIcon
                                icon={
                                  user.isActive &&
                                  !user.authDisabled
                                    ? faCheck
                                    : faBan
                                }
                              />

                              {user.isActive &&
                              !user.authDisabled
                                ? "نشط"
                                : "معطل"}
                            </span>

                            {user.isActive !==
                              !user.authDisabled && (
                              <small className="dashboard-users-warning">
                                يوجد اختلاف بين Auth وFirestore
                              </small>
                            )}
                          </div>
                        </td>

                        <td>
                          {formatDate(
                            user.lastLoginAt,
                          )}
                        </td>

                        <td>
                          {formatDate(
                            user.createdAt,
                          )}
                        </td>

                        <td>
                          <div className="dashboard-users-actions">
                            {(canUpdate ||
                              canManagePermissions) && (
                              <button
                                type="button"
                                className="dashboard-users-action-button"
                                onClick={() =>
                                  navigate(
                                    `/dashboard-users/${encodeURIComponent(
                                      user.uid,
                                    )}/edit`,
                                  )
                                }
                                title="تعديل"
                              >
                                <FontAwesomeIcon
                                  icon={
                                    faEdit
                                  }
                                />
                              </button>
                            )}

                            {canManageStatus && (
                              <button
                                type="button"
                                disabled={
                                  isActionLoading
                                }
                                className={
                                  user.isActive
                                    ? "dashboard-users-action-button dashboard-users-action-danger-soft"
                                    : "dashboard-users-action-button dashboard-users-action-success"
                                }
                                onClick={() =>
                                  handleToggleStatus(
                                    user,
                                  )
                                }
                                title={
                                  user.isActive
                                    ? "تعطيل"
                                    : "تفعيل"
                                }
                              >
                                <FontAwesomeIcon
                                  icon={
                                    user.isActive
                                      ? faBan
                                      : faCheck
                                  }
                                />
                              </button>
                            )}

                            {canDelete && (
                              <button
                                type="button"
                                disabled={
                                  isActionLoading
                                }
                                className="dashboard-users-action-button dashboard-users-action-danger"
                                onClick={() =>
                                  handleDelete(
                                    user,
                                  )
                                }
                                title="حذف نهائي"
                              >
                                <FontAwesomeIcon
                                  icon={
                                    faTrash
                                  }
                                />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  },
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="dashboard-users-pagination">
          <button
            type="button"
            disabled={
              cursorHistory.length ===
                0 ||
              loading
            }
            onClick={
              handlePreviousPage
            }
          >
            <FontAwesomeIcon
              icon={
                faChevronRight
              }
            />
            السابق
          </button>

          <span>
            {cursorHistory.length +
              1}
          </span>

          <button
            type="button"
            disabled={
              !hasMore ||
              !nextCursor ||
              loading
            }
            onClick={
              handleNextPage
            }
          >
            التالي
            <FontAwesomeIcon
              icon={
                faChevronLeft
              }
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardUsers;